import React, {
  createContext,
  useContext,
  useReducer,
  useRef,
  useEffect,
  useMemo,
  ReactNode,
  useState,
  useCallback,
} from 'react'
import {
  userManagementActions,
  userManagementInitialState,
  userManagementReducer,
  UserManagementState,
} from '@/reducer'
import { searchAllRoles, searchAllUsers } from '@/api/index'
import useSWR from 'swr'
import useAxios from '@/utilities/useAxios'

const DND_SERVICE = `${import.meta.env.VITE_DND_APP_BACKEND_URL}`

type UserManagementContextType = {
  state: UserManagementState
  actions: typeof userManagementActions
  id: number
  dispatch: React.Dispatch<any>
  users: any[] | undefined
  mutateUsers: () => Promise<any>
  roles: any[] | undefined
  mutateRoles: () => Promise<any>
  page: number
  setPage: React.Dispatch<React.SetStateAction<number>>
  rowsPerPage: number
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>
  isLoading: boolean
  optimizedSearchUsers: (keyword: string) => void
  userId: string
  totalElem: number
  optimizedFilterUsers: (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => void
  optimizedFilterRoleUsers: (roleName: string) => void
}

const UserManagementContext = createContext<
  UserManagementContextType | undefined
>(undefined)

export const useUserManagementContext = (): UserManagementContextType => {
  const context = useContext(UserManagementContext)
  if (!context) {
    throw new Error(
      'useUserManagementContext must be used within a UserManagementContextProvider',
    )
  }
  return context
}

type UserManagementContextProviderProps = {
  children: ReactNode
}

export const UserManagementContextProvider: React.FC<
  UserManagementContextProviderProps
> = ({ children }) => {
  const userId = localStorage.getItem('userId') || '0'

  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [keyword, setKeyword] = useState('')
  const [sortField, setSortField] = useState('dateCreated')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('DESC')

  const [startCreatedDate, setStartCreatedDate] = useState('')
  const [endCreatedDate, setEndCreatedDate] = useState('')
  const [startModifiedDate, setStartModifiedDate] = useState('')
  const [endModifiedDate, setEndModifiedDate] = useState('')

  const [roleName, setRoleName] = useState('')

  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [totalElem, setTotalElem] = useState(0)

  const getAllUsers = async () => {
    setIsLoading(true)
    try {
      const response = await searchAllUsers({
        keyword,
        startCreatedDate,
        endCreatedDate,
        startModifiedDate,
        endModifiedDate,
        page,
        size: rowsPerPage,
        roleName,
        sort: `${sortField},${sortDirection.toUpperCase()}`,
      })
      setTotalElem(response.data.totalElements)
      return response.data.data
    } finally {
      setIsLoading(false)
    }
  }

  const handleSort = (field: string, direction: 'asc' | 'desc') => {
    setSortField(field)
    setSortDirection(direction)
    setPage(1) // reset to first page when sorting changes
  }

  const debounce = <T extends (...args: any[]) => any>(
    func: T,
  ): ((...args: Parameters<T>) => void) => {
    let timer: NodeJS.Timeout | null = null

    return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
      if (timer) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        func.apply(this, args)
      }, 500)
    }
  }

  const handleChange = (keyword: string) => {
    setKeyword(keyword)
    setPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/user/search`, {
        params: {
          keyword: keyword,
          startCreatedDate: startCreatedDate,
          endCreatedDate: endCreatedDate,
          startModifiedDate: startModifiedDate,
          endModifiedDate: endModifiedDate,
          page: 1,
          size: rowsPerPage,
          roleIds: [],
          sort: 'dateCreated,DESC',
          pageable: {},
        },
      })
      .then((res) => res.data)
      .then((json) => setData(json?.data ?? []))
  }

  const handleDateFilter = (
    startCreatedDate: string,
    endCreatedDate: string,
    startModifiedDate: string,
    endModifiedDate: string,
  ) => {
    setStartCreatedDate(startCreatedDate)
    setEndCreatedDate(endCreatedDate)
    setStartModifiedDate(startModifiedDate)
    setEndModifiedDate(endModifiedDate)
    setPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/user/search`, {
        params: {
          keyword: keyword,
          startCreatedDate: startCreatedDate,
          endCreatedDate: endCreatedDate,
          startModifiedDate: startModifiedDate,
          endModifiedDate: endModifiedDate,
          page: 1,
          size: rowsPerPage,
          roleIds: [],
          sort: 'dateCreated,DESC',
          pageable: {},
        },
      })
      .then((res) => res.data)
      .then((json) => setData(json?.data ?? []))
  }

  const handleRoleFilter = (roleName: string) => {
    setRoleName(roleName)
    setPage(1)
    useAxios
      .get(`${DND_SERVICE}/v1/user/search`, {
        params: {
          keyword: keyword,
          startCreatedDate: startCreatedDate,
          endCreatedDate: endCreatedDate,
          startModifiedDate: startModifiedDate,
          endModifiedDate: endModifiedDate,
          page: 1,
          size: rowsPerPage,
          roleName: roleName,
          sort: 'dateCreated,DESC',
          pageable: {},
        },
      })
      .then((res) => res.data)
      .then((json) => setData(json?.data ?? []))
  }

  const optimizedSearchUsers = useCallback(debounce(handleChange), [])
  const optimizedFilterUsers = useCallback(debounce(handleDateFilter), [])
  const optimizedFilterRoleUsers = useCallback(debounce(handleRoleFilter), [])

  const getAllRoles = async () => {
    try {
      const response = await searchAllRoles({
        keyword: '',
        page: 1,
        size: 9999,
        sort: 'dateCreated,DESC',
        pageable: {},
      })

      return response.data.data
    } catch (error) {
      console.error('Failed to fetch users:', error)
      throw error
    }
  }

  const { data: users, mutate: mutateUsers } = useSWR(
    `/v1/user/search?keyword=${keyword}&startCreatedDate=${startCreatedDate}&endCreatedDate=${endCreatedDate}&startModifiedDate=${startModifiedDate}&endModifiedDate=${endModifiedDate}&page=${page}&size=${rowsPerPage}&roleName=${roleName}&sort=${sortField},${sortDirection}`,
    getAllUsers,
    { revalidateOnFocus: false },
  )

  const { data: roles, mutate: mutateRoles } = useSWR(
    '/v1/role/search',
    getAllRoles,
  )

  const [userManagementState, dispatchUserManagement] = useReducer(
    userManagementReducer,
    userManagementInitialState,
  )
  const stateRef = useRef<UserManagementState>(userManagementInitialState)

  const id = 2

  useEffect(() => {
    stateRef.current = userManagementState
  }, [userManagementState])

  useEffect(() => {
    mutateUsers()
  }, [data])

  const value = useMemo(
    () => ({
      state: userManagementState,
      actions: userManagementActions,
      id,
      dispatch: dispatchUserManagement,
      users,
      mutateUsers,
      page,
      setPage,
      rowsPerPage,
      setRowsPerPage,
      roles,
      mutateRoles,
      isLoading,
      optimizedSearchUsers,
      userId,
      totalElem,
      optimizedFilterUsers,
      optimizedFilterRoleUsers,
      handleSort,
    }),
    [
      userManagementState,
      id,
      users,
      page,
      rowsPerPage,
      isLoading,
      data,
      startCreatedDate,
      startModifiedDate,
      handleSort,
    ],
  )

  return (
    <UserManagementContext.Provider value={value}>
      {children}
    </UserManagementContext.Provider>
  )
}
